import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save,
  Eye,
  Image,
  Tag,
  Folder,
  Loader2,
  X,
  Plus,
  FileText,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category_id: '',
    tags: [],
    status: 'draft',
    seo_title: '',
    seo_description: ''
  });
  
  const [newTag, setNewTag] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showSeoSettings, setShowSeoSettings] = useState(false);

  const token = localStorage.getItem('examapp_token');

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchPost();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts/${id}?preview=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title || '',
          slug: data.slug || '',
          content: data.content || '',
          excerpt: data.excerpt || '',
          featured_image: data.featured_image || '',
          category_id: data.category_id || '',
          tags: data.tags || [],
          status: data.status || 'draft',
          seo_title: data.seo_title || '',
          seo_description: data.seo_description || ''
        });
      } else {
        toast.error('Post not found');
        navigate('/super-admin/blog');
      }
    } catch (error) {
      toast.error('Error loading post');
      navigate('/super-admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title)
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = async (publishStatus = null) => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Please enter content');
      return;
    }

    setSaving(true);
    
    const payload = {
      ...formData,
      status: publishStatus || formData.status
    };

    try {
      const url = isEditing 
        ? `${API_URL}/api/blog/posts/${id}`
        : `${API_URL}/api/blog/posts`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(isEditing ? 'Post updated!' : 'Post created!');
        if (publishStatus === 'published') {
          toast.success('Post is now live!');
        }
        navigate('/super-admin/blog');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save post');
      }
    } catch (error) {
      toast.error('Error saving post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/super-admin/blog')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">
                {isEditing ? 'Edit Post' : 'New Blog Post'}
              </h1>
              <p className="text-sm text-slate-500">
                {formData.status === 'published' ? 'Published' : 'Draft'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="preview-btn"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              data-testid="save-draft-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Draft
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              data-testid="publish-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              Publish
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Main Editor */}
          <div className="flex-1">
            {showPreview ? (
              /* Preview Mode */
              <div className="bg-white rounded-xl border border-slate-200 p-8">
                {formData.featured_image && (
                  <img
                    src={formData.featured_image}
                    alt=""
                    className="w-full h-64 object-cover rounded-xl mb-6"
                  />
                )}
                <h1 className="text-3xl font-bold text-slate-900 mb-4">{formData.title || 'Untitled Post'}</h1>
                <div className="flex items-center gap-4 mb-6 text-sm text-slate-500">
                  {formData.category_id && categories.find(c => c.id === formData.category_id) && (
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                      {categories.find(c => c.id === formData.category_id)?.name}
                    </span>
                  )}
                  <span>Draft Preview</span>
                </div>
                <div 
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.content || '<p>No content yet...</p>' }}
                />
                {formData.tags.length > 0 && (
                  <div className="flex items-center gap-2 mt-8 pt-6 border-t border-slate-200">
                    <Tag className="w-4 h-4 text-slate-400" />
                    {formData.tags.map(tag => (
                      <span key={tag} className="px-2 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Edit Mode */
              <div className="space-y-6">
                {/* Title */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder="Enter post title..."
                    className="w-full text-2xl font-bold text-slate-900 border-0 focus:outline-none focus:ring-0 placeholder:text-slate-300"
                    data-testid="title-input"
                  />
                  <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                    <span>Slug:</span>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      className="flex-1 px-2 py-1 border border-slate-200 rounded text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      data-testid="slug-input"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Content (HTML)
                  </label>
                  <p className="text-xs text-slate-500 mb-3">
                    Use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a href=""&gt;
                  </p>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Write your post content in HTML..."
                    className="w-full h-96 px-4 py-3 font-mono text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    data-testid="content-input"
                  />
                </div>

                {/* Excerpt */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Excerpt
                  </label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                    placeholder="Brief summary of the post (shown in listings)..."
                    className="w-full h-24 px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    data-testid="excerpt-input"
                  />
                </div>

                {/* SEO Settings */}
                <div className="bg-white rounded-xl border border-slate-200">
                  <button
                    onClick={() => setShowSeoSettings(!showSeoSettings)}
                    className="w-full p-4 flex items-center justify-between text-left"
                  >
                    <span className="font-medium text-slate-900">SEO Settings</span>
                    <span className="text-slate-400">{showSeoSettings ? '−' : '+'}</span>
                  </button>
                  {showSeoSettings && (
                    <div className="p-6 pt-0 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">SEO Title</label>
                        <input
                          type="text"
                          value={formData.seo_title}
                          onChange={(e) => setFormData(prev => ({ ...prev, seo_title: e.target.value }))}
                          placeholder="Custom title for search engines"
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          data-testid="seo-title-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">SEO Description</label>
                        <textarea
                          value={formData.seo_description}
                          onChange={(e) => setFormData(prev => ({ ...prev, seo_description: e.target.value }))}
                          placeholder="Meta description for search engines"
                          className="w-full h-20 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                          data-testid="seo-desc-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          {!showPreview && (
            <div className="w-80 space-y-6">
              {/* Featured Image */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Featured Image
                </label>
                {formData.featured_image ? (
                  <div className="relative">
                    <img
                      src={formData.featured_image}
                      alt=""
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <Image className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500 mb-3">Enter image URL</p>
                  </div>
                )}
                <input
                  type="text"
                  value={formData.featured_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  className="w-full mt-3 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="image-url-input"
                />
              </div>

              {/* Category */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  <Folder className="w-4 h-4 inline mr-1" />
                  Category
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="category-select"
                >
                  <option value="">No category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Tags
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tag..."
                    className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="tag-input"
                  />
                  <button
                    onClick={handleAddTag}
                    className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    data-testid="add-tag-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-sm rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {formData.tags.length === 0 && (
                    <p className="text-sm text-slate-400">No tags added</p>
                  )}
                </div>
              </div>

              {/* Quick Tips */}
              <div className="bg-indigo-50 rounded-xl p-5">
                <h4 className="font-medium text-indigo-900 mb-2">Writing Tips</h4>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li>• Use &lt;h2&gt; for main sections</li>
                  <li>• Add images with &lt;img src=""&gt;</li>
                  <li>• Create lists with &lt;ul&gt; &lt;li&gt;</li>
                  <li>• Bold text: &lt;strong&gt;</li>
                  <li>• Links: &lt;a href=""&gt;</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPostEditor;
